import type { SourceField, SourceFilter, TableSource, TriggerSource, TriggerSpec, TriggerSources } from './types'

type Copy = Record<string, string>

/** How a person picks what to watch. "objects" is a table source built from an ontology object type. */
export type SourceChoice = 'objects' | 'table' | 'metric' | 'analysis' | 'python' | 'sql'
export const SOURCE_CHOICES: SourceChoice[] = ['objects', 'table', 'metric', 'analysis', 'python', 'sql']

export type FieldKind = 'text' | 'number' | 'date' | 'bool'

/** What happened in the data. Each one is a plain fact about records, true for any base in Forge:
 *  new: a record that did not exist appears; changed: a chosen field changed in a record; reaches: a record comes to
 *  meet a condition on its fields (new records that already meet it included); leaves: it stops meeting it;
 *  removed: a record no longer appears; every: on each data update, every record (that meets the condition). */
export type EventChoice = 'new' | 'changed' | 'reaches' | 'leaves' | 'removed' | 'every'

export function eventsFor(choice: SourceChoice): EventChoice[] {
  if (choice === 'metric') return ['reaches', 'leaves', 'every']
  if (choice === 'objects' || choice === 'table') return ['new', 'changed', 'reaches', 'leaves', 'removed', 'every']
  return ['new', 'changed', 'removed', 'every']
}

/** Events that need a condition on the record's fields, and events that take one only to narrow the records. */
export const NEEDS_CONDITION: EventChoice[] = ['reaches', 'leaves']
export const OPTIONAL_CONDITION: EventChoice[] = ['changed', 'every']
const FIRE: Record<EventChoice, TriggerSpec['fire_on'][number]> = {
  new: 'entered', reaches: 'entered', changed: 'changed', leaves: 'exited', removed: 'exited', every: 'present',
}
export type Operator = SourceFilter['op']

export const OPERATORS: Record<FieldKind, Operator[]> = {
  text: ['eq', 'neq', 'in', 'not_in', 'contains', 'not_contains', 'empty', 'not_empty'],
  number: ['lte', 'lt', 'gte', 'gt', 'eq', 'neq', 'between', 'empty', 'not_empty'],
  date: ['within_days', 'older_than_days', 'after', 'before', 'empty', 'not_empty'],
  bool: ['is_true', 'is_false'],
}
export const NO_VALUE: Operator[] = ['empty', 'not_empty', 'is_true', 'is_false']

/** Forge column types (ClickHouse) and ontology data types, as the four kinds a filter knows. */
export function fieldKind(type: string | undefined): FieldKind {
  const t = (type ?? '').toLowerCase()
  if (/bool|uint8\b|flag/.test(t)) return 'bool'
  if (/date|time/.test(t)) return 'date'
  if (/int|float|decimal|double|number|numeric|currency|money|percent|quantity/.test(t)) return 'number'
  return 'text'
}

export interface PickField { key: string; field: string; label: string; type: string; technical?: boolean }

/** The fields a source offers to filters, group-bys and the identity, in business names when it has them. */
export function objectFields(source: TableSource): PickField[] {
  return source.fields.map((f) => ({ key: f.key, field: f.field, label: f.label || f.key, type: f.type || '' }))
}

export function tableFields(sources: TriggerSources, table: string): PickField[] {
  const found = sources.tables.find((t) => t.name === table)
  return (found?.fields ?? []).map((f) => ({ key: f.name, field: f.name, label: f.label || f.name, type: f.type, technical: f.technical }))
}

/** A good first guess at what identifies a row of a table: an id column, else every *_id column. */
export function guessIdentity(fields: PickField[]): string[] {
  const usable = fields.filter((f) => !f.technical)
  const id = usable.find((f) => f.field === 'id')
  if (id) return [id.key]
  const ids = usable.filter((f) => /(^|_)(id|uuid|key|code|codigo|numero|number)$/i.test(f.field)).slice(0, 3)
  return ids.map((f) => f.key)
}

export interface Draft {
  originalSpec?: TriggerSpec
  choice: SourceChoice
  objectTypeId: string
  table: string
  identity: string[]
  carry: string[]
  filters: SourceFilter[]
  watch: string[]
  measure: { agg: 'count' | 'count_distinct' | 'sum' | 'avg' | 'min' | 'max'; field: string }
  groupBy: string[]
  threshold: { op: 'gt' | 'gte' | 'lt' | 'lte' | 'eq' | 'neq'; value: string }
  metricOn: 'objects' | 'table'
  analysisId: string
  analysisComputation: string
  analysisTable: string
  analysisFilters: Record<string, string>
  code: string
  output: string
  sql: string
  entity: string[]
  event: EventChoice
  fireNow: boolean
  target: 'praxis' | 'none'
  workflow: string
  batch: boolean
  extra: Array<{ key: string; value: string }>
  every: number
  requireSync: boolean
  allowPartial: boolean
  staleness: number
  exitShare: number
  maxEvents: number
  minRows: string
  maxRows: number
}

export const PYTHON_TEMPLATE = `def run(data, params, ctx):
    # ctx.sql() lê o Forge como o projeto. Devolva uma tabela: cada linha é um item que atende.
    linhas = ctx.sql("SELECT id, valor, atualizado_em FROM mart_exemplo")
    return linhas[linhas["valor"] > 100]
`

export function emptyDraft(praxisAvailable: boolean): Draft {
  return {
    choice: 'objects', objectTypeId: '', table: '', identity: [], carry: [], filters: [], watch: [],
    measure: { agg: 'count', field: '' }, groupBy: [], threshold: { op: 'gt', value: '' }, metricOn: 'objects',
    analysisId: '', analysisComputation: '', analysisTable: '', analysisFilters: {},
    code: PYTHON_TEMPLATE, output: 'main', sql: '', entity: [],
    event: 'new', fireNow: false,
    target: praxisAvailable ? 'praxis' : 'none', workflow: '', batch: false, extra: [],
    every: 15, requireSync: true, allowPartial: false, staleness: 36, exitShare: 0.8, maxEvents: 200, minRows: '', maxRows: 50000,
  }
}

/** The event a stored spec means: the same comparison, read back as what happened in the data. */
export function eventFrom(spec: TriggerSpec): EventChoice {
  const kind = spec.fire_on[0] ?? 'entered'
  const conditioned = spec.source.kind === 'metric' || ((spec.source as { filters?: unknown[] }).filters?.length ?? 0) > 0
  if (kind === 'present') return 'every'
  if (kind === 'changed') return 'changed'
  if (kind === 'exited') return conditioned ? 'leaves' : 'removed'
  return conditioned ? 'reaches' : 'new'
}

/** A stored spec back into the editor's choices. */
export function draftFrom(spec: TriggerSpec, sources: TriggerSources, praxisAvailable: boolean): Draft {
  const d = emptyDraft(praxisAvailable)
  const s = spec.source
  const gate = spec.gate ?? {}
  Object.assign(d, {
    originalSpec: spec,
    event: eventFrom(spec), fireNow: spec.start === 'all', watch: spec.watch ?? [],
    every: spec.schedule?.check_every_minutes ?? 15, requireSync: gate.require_sync_success ?? true,
    allowPartial: gate.allow_partial_sync ?? false, staleness: gate.max_staleness_hours ?? 36, exitShare: gate.max_exit_share ?? 0.8,
    maxEvents: gate.max_events ?? 200, minRows: gate.min_rows != null ? String(gate.min_rows) : '', maxRows: s.max_rows ?? 50000,
  })
  if (spec.target.kind === 'praxis') {
    Object.assign(d, { target: 'praxis', workflow: spec.target.workflow, batch: Boolean(spec.target.batch),
      extra: Object.entries(spec.target.input?.static ?? {}).map(([key, value]) => ({ key, value: String(value) })) })
  } else d.target = 'none'
  const objectId = 'object_type' in s && s.object_type?.id ? s.object_type.id : ''
  const known = objectId && sources.object_types.some((o) => o.id === objectId)
  if (s.kind === 'table') {
    Object.assign(d, { choice: known ? 'objects' : 'table', objectTypeId: known ? objectId : '', table: s.table,
      identity: s.identity, carry: s.fields.map((f) => f.key), filters: s.filters ?? [] })
  } else if (s.kind === 'metric') {
    Object.assign(d, { choice: 'metric', metricOn: known ? 'objects' : 'table', objectTypeId: known ? objectId : '', table: s.table,
      measure: { agg: s.measure.agg, field: s.measure.field ?? '' }, groupBy: s.group_by.map((g) => g.field),
      threshold: { op: s.threshold.op, value: String(s.threshold.value) }, filters: s.filters ?? [] })
  } else if (s.kind === 'computation') {
    Object.assign(d, { choice: 'analysis', analysisId: s.publication_id, analysisComputation: s.computation, analysisTable: s.table,
      analysisFilters: Object.fromEntries(Object.entries(s.filters ?? {}).map(([k, v]) => [k, String(v)])), entity: spec.entity })
  } else if (s.kind === 'python') {
    Object.assign(d, { choice: 'python', code: s.code, output: s.output ?? 'main', entity: spec.entity })
  } else {
    Object.assign(d, { choice: 'sql', sql: s.sql, entity: spec.entity })
  }
  return d
}

function metricFields(d: Draft, sources: TriggerSources): PickField[] {
  const object = sources.object_types.find((o) => o.id === d.objectTypeId)
  return d.metricOn === 'objects' && object?.source ? objectFields(object.source) : draftTableFields(d, sources)
}

function draftTableFields(d: Draft, sources: TriggerSources): PickField[] {
  const original = d.originalSpec?.source
  const previous = original?.kind === 'table' ? original.fields : original?.kind === 'metric' ? original.group_by : []
  return tableFields(sources, d.table).map((field) => {
    const saved = original && 'table' in original && original.table === d.table ? previous.find((f) => f.field === field.field) : undefined
    return saved ? { ...field, ...saved, label: saved.label || field.label, type: saved.type || field.type } : field
  })
}

/** The fields filters can use for the current choice. */
export function filterFields(d: Draft, sources: TriggerSources): PickField[] {
  if (d.choice === 'objects') {
    const object = sources.object_types.find((o) => o.id === d.objectTypeId)
    return object?.source ? objectFields(object.source) : []
  }
  if (d.choice === 'table') return draftTableFields(d, sources)
  if (d.choice === 'metric') return metricFields(d, sources)
  return []
}

function asSourceField(f: PickField): SourceField {
  return { key: f.key, field: f.field, label: f.label, type: f.type }
}

/** The spec Forge stores, or the reason it is not complete yet (shown next to the block that needs it). */
export function buildSpec(d: Draft, sources: TriggerSources, copy: Copy): { spec?: TriggerSpec; preview?: TriggerSpec; missing?: string } {
  let source: TriggerSource
  let entity: string[] = []
  const filters = d.filters.filter((f) => f.field)
  if (d.choice === 'objects') {
    const object = sources.object_types.find((o) => o.id === d.objectTypeId)
    if (!object?.source) return { missing: copy.missing_object }
    source = { ...object.source, filters }
  } else if (d.choice === 'table') {
    const fields = draftTableFields(d, sources)
    if (!d.table || !fields.length) return { missing: copy.missing_table }
    if (!d.identity.length) return { missing: copy.missing_identity }
    const keep = new Set([...d.identity, ...(d.carry.length ? d.carry : fields.filter((f) => !f.technical).map((f) => f.key))])
    const carried = fields.filter((f) => keep.has(f.key)).slice(0, 64)
    const label = sources.tables.find((t) => t.name === d.table)?.label
    source = { kind: 'table', table: d.table, label, fields: carried.map(asSourceField), identity: d.identity, filters }
  } else if (d.choice === 'metric') {
    const object = d.metricOn === 'objects' ? sources.object_types.find((o) => o.id === d.objectTypeId) : undefined
    const table = object?.source?.table ?? d.table
    const fields = metricFields(d, sources)
    if (!table || !fields.length) return { missing: d.metricOn === 'objects' ? copy.missing_object : copy.missing_table }
    if (d.measure.agg !== 'count' && !d.measure.field) return { missing: copy.missing_measure }
    if (d.threshold.value.trim() === '' || Number.isNaN(Number(d.threshold.value))) return { missing: copy.missing_threshold }
    const byField = new Map(fields.map((f) => [f.field, f]))
    const measureField = d.measure.field ? byField.get(d.measure.field) : undefined
    source = {
      kind: 'metric', table, label: object?.name ?? sources.tables.find((t) => t.name === table)?.label,
      ...(object?.source?.object_type ? { object_type: object.source.object_type } : {}),
      measure: { agg: d.measure.agg, ...(d.measure.agg !== 'count' ? { field: d.measure.field } : {}),
        label: `${copy[`agg_${d.measure.agg}`]}${measureField ? ` ${measureField.label.toLowerCase()}` : ''}` },
      group_by: d.groupBy.map((field) => byField.get(field)).filter((f): f is PickField => Boolean(f)).map(asSourceField),
      filters, threshold: { op: d.threshold.op, value: Number(d.threshold.value) },
    }
  } else if (d.choice === 'analysis') {
    const analysis = (sources.analyses ?? []).find((a) => a.publication_id === d.analysisId)
    if (!analysis || !d.analysisComputation) return { missing: copy.missing_analysis }
    if (!d.entity.length) return { missing: copy.missing_identity }
    const savedFilters = d.originalSpec?.source.kind === 'computation' ? d.originalSpec.source.filters ?? {} : {}
    const filters = Object.fromEntries(Object.entries(d.analysisFilters).filter(([, v]) => v !== '').map(([key, value]) =>
      [key, key in savedFilters && String(savedFilters[key]) === value ? savedFilters[key] : value]))
    source = {
      kind: 'computation', organization_id: analysis.organization_id, project_id: analysis.project_id,
      publication_id: analysis.publication_id, version: analysis.version, data_principal_id: analysis.data_principal_id,
      data_policy_version: analysis.data_policy_version, computation: d.analysisComputation,
      table: d.analysisTable || analysis.computations.find((c) => c.name === d.analysisComputation)?.tables[0] || 'main',
      filters, label: d.analysisComputation, publication_title: analysis.title,
    }
    entity = d.entity
  } else if (d.choice === 'python') {
    if (!/^def run\s*\(/m.test(d.code)) return { missing: copy.missing_run }
    if (!d.entity.length) return { missing: copy.missing_identity }
    source = { kind: 'python', code: d.code, output: d.output || 'main' }
    entity = d.entity
  } else {
    if (!/^\s*(select|with)\b/i.test(d.sql)) return { missing: copy.missing_sql }
    if (!d.entity.length) return { missing: copy.missing_identity }
    source = { kind: 'sql', sql: d.sql.trim() }
    entity = d.entity
  }
  // Preserve agent-authored parameters and labels that the simple editor does
  // not expose. Explicit source edits still override the saved fields.
  if (d.originalSpec?.source.kind === source.kind) source = { ...d.originalSpec.source, ...source } as TriggerSource
  source.max_rows = d.maxRows
  const event = eventsFor(d.choice).includes(d.event) ? d.event : eventsFor(d.choice)[0]
  if (source.kind === 'table') {
    entity = source.identity
    if (NEEDS_CONDITION.includes(event) && !filters.length) return { missing: copy.missing_condition }
    if (event === 'new' || event === 'removed') source = { ...source, filters: [] }
  }
  if (event === 'changed' && !d.watch.length && !d.originalSpec) return { missing: copy.missing_watch }
  if (source.kind === 'metric') entity = source.group_by.length ? source.group_by.map((field) => field.key) : ['escopo']
  const originalValues = d.originalSpec?.target.kind === 'praxis' ? d.originalSpec.target.input?.static ?? {} : {}
  const extra = Object.fromEntries(d.extra.filter((e) => e.key.trim()).map((e) => {
    const key = e.key.trim()
    return [key, key in originalValues && String(originalValues[key]) === e.value ? originalValues[key] : e.value]
  }))
  const spec: TriggerSpec = {
      source,
      entity,
      watch: d.watch.filter((w) => !entity.includes(w) && !(source.kind === 'table' && source.identity.includes(w))),
      fire_on: d.originalSpec && event === eventFrom(d.originalSpec) ? d.originalSpec.fire_on : [FIRE[event]],
      start: d.fireNow ? 'all' : 'now',
      schedule: { check_every_minutes: d.every },
      gate: { require_sync_success: d.requireSync, allow_partial_sync: d.allowPartial, max_staleness_hours: d.staleness,
        max_exit_share: d.exitShare, max_events: d.maxEvents, min_rows: d.minRows.trim() ? Number(d.minRows) : null },
      target: d.target === 'praxis' ? { kind: 'praxis', workflow: d.workflow, batch: d.batch, input: { static: extra } } : { kind: 'none' },
  }
  // the live view needs only what to watch; saving needs the action too
  if (d.target === 'praxis' && !d.workflow) return { preview: { ...spec, target: { kind: 'none' } }, missing: copy.missing_workflow }
  return { spec, preview: spec }
}

// ------------------------------------------------------------------------------------------- in words

const fill = (text: string, values: Record<string, string | number>) =>
  Object.entries(values).reduce((out, [key, value]) => out.split(`{${key}}`).join(String(value)), text ?? '')

export function describeFilter(f: SourceFilter, copy: Copy): string {
  const name = f.label || f.field
  const op = copy[`op_${f.op}`] ?? f.op
  if (NO_VALUE.includes(f.op)) return `${name} ${op}`
  const value = Array.isArray(f.value) ? (f.op === 'between' ? f.value.join(` ${copy.and} `) : f.value.join(', ')) : String(f.value ?? '')
  if (f.op === 'within_days' || f.op === 'older_than_days') return `${name} ${fill(op, { days: value })}`
  return `${name} ${op} ${value}`
}

/** "Fatura onde Vencimento há mais de 5 dias" / "Quantidade de Chamado por Fila > 10" / "Análise em Python". */
export function describeSource(source: TriggerSource, copy: Copy): string {
  const where = (filters?: SourceFilter[]) =>
    filters?.length ? ` ${copy.where} ${filters.map((f) => describeFilter(f, copy)).join(` ${copy.and} `)}` : ''
  if (source.kind === 'table') return `${source.object_type?.name ?? source.label ?? source.table}${where(source.filters)}`
  if (source.kind === 'metric') {
    const by = source.group_by.length ? ` ${copy.per} ${source.group_by.map((g) => g.label || g.key).join(', ')}` : ''
    const what = source.object_type?.name ?? source.label ?? source.table
    return `${source.measure.label || copy[`agg_${source.measure.agg}`]} ${copy.of} ${what}${by} ${copy[`op_${source.threshold.op}`]} ${source.threshold.value}${where(source.filters)}`
  }
  if (source.kind === 'python') return source.label || copy.source_python_label
  if (source.kind === 'computation') {
    return `${source.label || source.computation}${source.table !== 'main' ? ` · ${source.table}` : ''} ${copy.in_dashboard} ${source.publication_title || copy.dashboard}`
  }
  return source.label || copy.source_sql_label
}

export function sourceKindLabel(source: TriggerSource, copy: Copy): string {
  if (source.kind === 'table') return source.object_type ? copy.choice_objects : copy.choice_table
  if (source.kind === 'computation') return copy.choice_analysis
  return copy[`choice_${source.kind}`]
}

/** What each event is about, in the source's words: "objeto", "linha", "grupo". */
export function thingWord(source: TriggerSource, copy: Copy): string {
  if (source.kind === 'metric') return source.group_by.length ? copy.thing_group : copy.thing_indicator
  if (source.kind === 'table' && source.object_type) return source.object_type.name.toLowerCase()
  return copy.thing_row
}

/** The condition built on the record's fields, as people say it: "Status é cancelado e Valor maior que 1000";
 *  for an indicator, its limit: "Quantidade maior que 10, por Região". Null when there is none. */
export function conditionText(source: TriggerSource, copy: Copy): string | null {
  if (source.kind === 'table') {
    return source.filters?.length ? source.filters.map((f) => describeFilter(f, copy)).join(` ${copy.and} `) : null
  }
  if (source.kind === 'metric') {
    const by = source.group_by.length ? `, ${copy.per} ${source.group_by.map((g) => g.label || g.key).join(', ')}` : ''
    return `${copy[`op_${source.threshold.op}`]} ${source.threshold.value}${by}`
  }
  return null
}

function sourceName(source: TriggerSource, copy: Copy): string {
  if (source.kind === 'table') return source.object_type?.name ?? source.label ?? source.table
  if (source.kind === 'metric') return `${source.measure.label || copy[`agg_${source.measure.agg}`]} ${copy.of} ${source.object_type?.name ?? source.label ?? source.table}`
  if (source.kind === 'computation') return `${source.label || source.computation}${source.publication_title ? ` (${source.publication_title})` : ''}`
  return source.kind === 'python' ? copy.source_python_label : copy.source_sql_label
}

function result(source: TriggerSource | null): boolean {
  return Boolean(source && source.kind !== 'table' && source.kind !== 'metric')
}

/** Name and meaning of an event for the source being watched. */
export function eventTitle(event: EventChoice, choice: SourceChoice, copy: Copy): string {
  const variant = choice === 'metric' ? '_metric' : choice === 'objects' || choice === 'table' ? '' : '_result'
  return copy[`event_${event}${variant}`] ?? copy[`event_${event}`]
}

export function eventHelp(event: EventChoice, choice: SourceChoice, copy: Copy): string {
  const variant = choice === 'metric' ? '_metric' : choice === 'objects' || choice === 'table' ? '' : '_result'
  return copy[`event_${event}${variant}_help`] ?? copy[`event_${event}_help`]
}

/** The whole trigger in one sentence, from the facts it watches:
 *  "Quando um registro de Pedido passar a ter Status é cancelado, iniciar o workflow estornar." */
export function triggerSentence(spec: TriggerSpec, copy: Copy, labels: Record<string, string> = {}): string {
  const source = spec.source
  const event = eventFrom(spec)
  const name = sourceName(source, copy)
  const condition = conditionText(source, copy)
  const fields = (spec.watch ?? []).map((key) => labels[key] ?? (source.kind === 'table' ? source.fields.find((f) => f.key === key)?.label : null) ?? key)
  const action = spec.target.kind === 'praxis' ? fill(copy.sentence_starts, { workflow: spec.target.workflow || '…' }) : copy.sentence_records
  const key = source.kind === 'metric' ? `sentence_${event}_metric` : result(source) ? `sentence_${event}_result` : `sentence_${event}`
  const only = condition && source.kind === 'table' && OPTIONAL_CONDITION.includes(event) ? fill(copy.sentence_only, { condition }) : ''
  return fill(copy[key] ?? copy[`sentence_${event}`] ?? '', {
    source: name, condition: condition ?? '', fields: fields.length ? fields.join(', ') : copy.any_field, only, action,
  })
}
